import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { useToast } from '../../components/ui/use-toast';
import { Search, ChevronLeft, History, PackagePlus, SlidersHorizontal, ClipboardCheck, AlertTriangle } from 'lucide-react';
import { useRestaurant } from '../../context/RestaurantContext';
import { locationsService } from '../../services/locationsService';
import {
  listStock, restock as apiRestock, adjust as apiAdjust,
  updateSettings, getMovements, getSummary,
} from '../../services/barInventoryService';

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const fmtMl = (n) => `${Math.round(Number(n || 0))} ml`;
const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Open bottle as a "tenth-of-a-bottle" figure, the bar-standard count unit.
const tenths = (openMl, sizeMl) => {
  const size = Number(sizeMl || 0);
  if (size <= 0) return '0';
  return (Number(openMl || 0) / size).toFixed(1);
};
const REASON_LABEL = { sale: 'Sale', restock: 'Restock', adjustment: 'Adjustment' };

export default function BarInventoryManagement() {
  const { restaurantId: urlRestaurantId, locationId } = useParams();
  const { restaurantId: ctxRestaurantId } = useRestaurant();
  const restaurantId = urlRestaurantId || ctxRestaurantId;
  const navigate = useNavigate();
  const { toast } = useToast();

  const [locationName, setLocationName] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [summary, setSummary] = useState(null);
  const [range, setRange] = useState({ from: daysAgoISO(30), to: todayISO() });

  // Dialog state — each holds the row being acted on (plus form fields), or null.
  const [restocking, setRestocking] = useState(null); // { row, bottles, unit_cost, note }
  const [adjusting, setAdjusting] = useState(null);    // { row, full_bottles, open_tenths, note }
  const [settingsItem, setSettingsItem] = useState(null); // { row, low, bottle_size }
  const [movements, setMovements] = useState(null);    // { row, list, loading }
  const [saving, setSaving] = useState(false);

  const loadStock = useCallback(async () => {
    if (!restaurantId || !locationId) return;
    setLoading(true);
    try {
      const data = await listStock(restaurantId, locationId);
      setRows(data);
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to load bar stock', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, locationId, toast]);

  const loadSummary = useCallback(async () => {
    if (!restaurantId || !locationId) return;
    try {
      setSummary(await getSummary(restaurantId, locationId, range));
    } catch {
      setSummary(null);
    }
  }, [restaurantId, locationId, range]);

  useEffect(() => { loadStock(); }, [loadStock]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  useEffect(() => {
    let alive = true;
    if (!restaurantId || !locationId) return undefined;
    locationsService.list(restaurantId)
      .then((list) => {
        if (!alive) return;
        const loc = (Array.isArray(list) ? list : []).find((l) => String(l.id) === String(locationId));
        setLocationName(loc?.name || '');
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [restaurantId, locationId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.name} ${r.brand}`.toLowerCase().includes(q));
  }, [rows, search]);

  // ---- Restock --------------------------------------------------------------
  const openRestock = (row) => setRestocking({ row, bottles: '', unit_cost: row.cost_per_bottle || '', note: '' });
  const submitRestock = async () => {
    if (!(Number(restocking.bottles) > 0)) {
      toast({ title: 'Bottles required', description: 'Enter how many bottles arrived.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await apiRestock(restaurantId, locationId, restocking.row.dish_id, {
        bottles: Number(restocking.bottles),
        unit_cost: restocking.unit_cost === '' ? 0 : Number(restocking.unit_cost),
        note: restocking.note || '',
      });
      toast({ title: 'Restocked', description: `${restocking.bottles} bottle(s) added.` });
      setRestocking(null);
      loadStock(); loadSummary();
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to restock', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ---- Adjust (set actual on-hand) -----------------------------------------
  const openAdjust = (row) => setAdjusting({
    row,
    full_bottles: String(row.full_bottles ?? 0),
    open_tenths: tenths(row.open_bottle_ml, row.bottle_size_ml),
    note: '',
  });
  const submitAdjust = async () => {
    const size = Number(adjusting.row.bottle_size_ml || 0);
    const openMl = Math.max(0, Number(adjusting.open_tenths || 0)) * size;
    setSaving(true);
    try {
      await apiAdjust(restaurantId, locationId, adjusting.row.dish_id, {
        full_bottles: Math.max(0, Number(adjusting.full_bottles || 0)),
        open_bottle_ml: openMl,
        note: adjusting.note || '',
      });
      toast({ title: 'Stock updated', description: 'Counted on-hand saved.' });
      setAdjusting(null);
      loadStock(); loadSummary();
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to adjust', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ---- Settings (par + bottle size) ----------------------------------------
  const openSettings = (row) => setSettingsItem({
    row,
    low: String(row.low_stock_threshold_bottles ?? 0),
    bottle_size: String(row.bottle_size_ml ?? 0),
  });
  const submitSettings = async () => {
    setSaving(true);
    try {
      await updateSettings(restaurantId, locationId, settingsItem.row.dish_id, {
        low_stock_threshold_bottles: Math.max(0, Number(settingsItem.low || 0)),
        bottle_size_ml: Number(settingsItem.bottle_size || 0),
      });
      toast({ title: 'Saved', description: 'Item settings updated.' });
      setSettingsItem(null);
      loadStock(); loadSummary();
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ---- Movements ------------------------------------------------------------
  const openMovements = async (row) => {
    setMovements({ row, list: [], loading: true });
    try {
      const data = await getMovements(restaurantId, locationId, row.dish_id);
      setMovements({ row, list: data.results || [], loading: false });
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to load movements', variant: 'destructive' });
      setMovements(null);
    }
  };

  if (!restaurantId || !locationId) {
    return <div className="p-6 text-muted-foreground">No location selected.</div>;
  }

  const pourCostTone = (pct) => {
    if (pct == null) return 'text-muted-foreground';
    if (pct <= 25) return 'text-success';
    if (pct <= 30) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <div className="w-full p-6 space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate(`/restaurant/${restaurantId}/bar-inventory`)}
          className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground font-mono mb-1 hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" /> Bar Inventory · {locationName || 'Location'}
        </button>
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">Bar Inventory</h1>
      </div>

      {/* ---------- Pour-cost summary ---------- */}
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle>Pour cost</CardTitle>
          </div>
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Input type="date" value={range.from} max={range.to}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input type="date" value={range.to} min={range.from}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="h-9" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Metric label="Pour cost" value={summary?.pour_cost_pct != null ? `${summary.pour_cost_pct}%` : '—'} tone={pourCostTone(summary?.pour_cost_pct)} />
            <Metric label="Liquor sales" value={summary ? money(summary.liquor_sales) : '—'} />
            <Metric label="Cost poured" value={summary ? money(summary.cogs_poured) : '—'} />
            <Metric label="Value on hand" value={summary ? money(summary.value_on_hand) : '—'} />
            <Metric label="Low stock" value={summary?.low_stock_count ?? '—'} tone={summary?.low_stock_count ? 'text-amber-600' : undefined} />
          </div>
        </CardContent>
      </Card>

      {/* ---------- Stock table ---------- */}
      <Card>
        <CardHeader>
          <CardTitle>Stock on hand</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search liquor…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Bottle</TableHead>
                  <TableHead className="text-right">Full</TableHead>
                  <TableHead className="text-right">Open</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Pegs</TableHead>
                  <TableHead className="text-right">Cost/btl</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-500">Loading…</TableCell></TableRow>
                )}
                {!loading && filtered.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-500">
                    No liquor items at this location. Add liquor items under All Dishes first.
                  </TableCell></TableRow>
                )}
                {filtered.map((r) => (
                  <TableRow key={r.dish_id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {r.name}
                        {r.is_low && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded-full font-medium bg-amber-100 text-amber-700">
                            <AlertTriangle className="h-3 w-3" /> Low
                          </span>
                        )}
                      </div>
                      {r.brand && <div className="text-xs text-slate-400">{r.brand}</div>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm text-slate-600">{r.bottle_size_ml ? fmtMl(r.bottle_size_ml) : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.full_bottles}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {tenths(r.open_bottle_ml, r.bottle_size_ml)}
                      <span className="text-xs text-slate-400"> ({fmtMl(r.open_bottle_ml)})</span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMl(r.total_ml)}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.servings_remaining}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.cost_per_bottle ? money(r.cost_per_bottle) : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.value_on_hand)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" title="Restock" onClick={() => openRestock(r)}><PackagePlus className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" title="Count / adjust" onClick={() => openAdjust(r)}><ClipboardCheck className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" title="Movements" onClick={() => openMovements(r)}><History className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" title="Settings" onClick={() => openSettings(r)}><SlidersHorizontal className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ---------- Restock dialog ---------- */}
      <Dialog open={restocking !== null} onOpenChange={(o) => !o && setRestocking(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Restock — {restocking?.row.name}</DialogTitle>
          </DialogHeader>
          {restocking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bottles *</Label>
                  <Input type="number" min="0" step="1" value={restocking.bottles}
                    onChange={(e) => setRestocking({ ...restocking, bottles: e.target.value })} placeholder="e.g., 6" />
                </div>
                <div className="space-y-2">
                  <Label>Cost / bottle</Label>
                  <Input type="number" min="0" step="any" value={restocking.unit_cost}
                    onChange={(e) => setRestocking({ ...restocking, unit_cost: e.target.value })} placeholder="optional" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Input value={restocking.note} onChange={(e) => setRestocking({ ...restocking, note: e.target.value })} placeholder="Supplier / invoice #" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestocking(null)}>Cancel</Button>
            <Button onClick={submitRestock} disabled={saving}>{saving ? 'Saving…' : 'Add stock'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Adjust dialog ---------- */}
      <Dialog open={adjusting !== null} onOpenChange={(o) => !o && setAdjusting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Count — {adjusting?.row.name}</DialogTitle>
          </DialogHeader>
          {adjusting && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full bottles</Label>
                  <Input type="number" min="0" step="1" value={adjusting.full_bottles}
                    onChange={(e) => setAdjusting({ ...adjusting, full_bottles: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Open bottle (tenths)</Label>
                  <Input type="number" min="0" max="1" step="0.1" value={adjusting.open_tenths}
                    onChange={(e) => setAdjusting({ ...adjusting, open_tenths: e.target.value })} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                ≈ {fmtMl(Math.max(0, Number(adjusting.open_tenths || 0)) * Number(adjusting.row.bottle_size_ml || 0))} in the open bottle.
              </p>
              <div className="space-y-2">
                <Label>Reason / note</Label>
                <Textarea rows={2} value={adjusting.note} onChange={(e) => setAdjusting({ ...adjusting, note: e.target.value })} placeholder="Physical count, breakage, comp…" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjusting(null)}>Cancel</Button>
            <Button onClick={submitAdjust} disabled={saving}>{saving ? 'Saving…' : 'Save count'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Settings dialog ---------- */}
      <Dialog open={settingsItem !== null} onOpenChange={(o) => !o && setSettingsItem(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Settings — {settingsItem?.row.name}</DialogTitle>
          </DialogHeader>
          {settingsItem && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Low-stock alert at (bottles)</Label>
                <Input type="number" min="0" step="1" value={settingsItem.low}
                  onChange={(e) => setSettingsItem({ ...settingsItem, low: e.target.value })} placeholder="0 = no alert" />
              </div>
              <div className="space-y-2">
                <Label>Bottle size (ml)</Label>
                <Input type="number" min="0" step="any" value={settingsItem.bottle_size}
                  onChange={(e) => setSettingsItem({ ...settingsItem, bottle_size: e.target.value })} placeholder="e.g., 750" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsItem(null)}>Cancel</Button>
            <Button onClick={submitSettings} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Movements dialog ---------- */}
      <Dialog open={movements !== null} onOpenChange={(o) => !o && setMovements(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{movements?.row.name} — movements</DialogTitle>
          </DialogHeader>
          {movements?.loading ? (
            <div className="py-8 text-center text-slate-500">Loading…</div>
          ) : (
            <div className="border rounded-lg overflow-hidden max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader className="bg-slate-50 sticky top-0">
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Change</TableHead>
                    <TableHead className="text-right">After</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead>Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!movements?.list || movements.list.length === 0) && (
                    <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400 text-sm">No movements yet.</TableCell></TableRow>
                  )}
                  {(movements?.list || []).map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="text-sm text-slate-600">{m.created_at ? new Date(m.created_at).toLocaleString() : '—'}</TableCell>
                      <TableCell>{REASON_LABEL[m.reason] || m.reason}</TableCell>
                      <TableCell className={`text-right tabular-nums ${m.change_ml < 0 ? 'text-red-600' : 'text-success'}`}>
                        {m.change_ml > 0 ? '+' : ''}{fmtMl(m.change_ml)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-slate-600">
                        {m.full_bottles_after} + {fmtMl(m.open_bottle_ml_after)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{m.cost_value ? money(m.cost_value) : '—'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{m.note || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovements(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground font-mono">{label}</div>
      <div className={`text-2xl font-display font-semibold tabular-nums ${tone || 'text-foreground'}`}>{value}</div>
    </div>
  );
}
